'use client';

import { AppDispatch, RootState } from '@/lib/store';
import { dismissAlert, fetchGlobalAlerts } from '@/lib/store/core/alerts';
import { getDismissedAlertsCookie } from '@/lib/utils/alert-cookies';
import { ErrorBoundary } from 'next/dist/client/components/error-boundary';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GlobalAlertsDisplay } from './alert-portal';

interface GlobalAlertsProviderProps {
  refreshInterval?: number; // Interval in milliseconds to refresh alerts
}

/**
 * Provider component that fetches global alerts from the server
 * and initializes them in the Redux store
 */
export function GlobalAlertsProvider({
  refreshInterval = 60000  // Default to 1 minute
}: GlobalAlertsProviderProps) {
  const dispatch = useDispatch<AppDispatch>();
  const alerts = useSelector((state: RootState) => state.alerts.alerts);
  const dismissedAlerts = useSelector((state: RootState) => state.alerts.dismissedAlerts);
  const [hasError, setHasError] = useState(false);

  // Fetch alerts on initial load and periodically
  useEffect(() => {
    const fetchAndFilterAlerts = async () => {
      try {
        // Dispatch the action to fetch alerts
        await dispatch(fetchGlobalAlerts());
        setHasError(false);
      } catch (error) {
        console.error('Error fetching alerts:', error);
        setHasError(true);
      }
    };

    // Initial fetch
    fetchAndFilterAlerts();

    // Set up interval for periodic refreshes
    const intervalId = setInterval(() => {
      fetchAndFilterAlerts();
    }, refreshInterval);

    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, [dispatch, refreshInterval]);

  // Sync cookie-dismissed alerts with Redux store
  useEffect(() => {
    try {
      const cookieDismissedAlerts = getDismissedAlertsCookie();

      // For each alert that's dismissed in cookies but not in Redux store
      if (cookieDismissedAlerts.length > 0) {
        cookieDismissedAlerts.forEach(alertId => {
          if (dismissedAlerts && !dismissedAlerts.includes(alertId)) {
            dispatch(dismissAlert(alertId));
          }
        });
      }
    } catch (error) {
      console.error('Error syncing dismissed alerts:', error);
    }
  }, [alerts, dismissedAlerts, dispatch]);

  // Wrap in Next.js error boundary to prevent alerts from breaking the app
  return (
    <ErrorBoundary errorComponent={() => null}>
      <GlobalAlertsDisplay />
    </ErrorBoundary>
  );
}

export default GlobalAlertsProvider;
